import { useState } from 'react';
import { editorReview, comments as commentsData } from '@/mocks/data';

export default function EditorReview() {
  const [activeTab, setActiveTab] = useState<'review' | 'comments'>('review');
  const [commentIndex, setCommentIndex] = useState(0);

  const currentComment = commentsData[commentIndex];

  const handlePrevComment = () => {
    setCommentIndex((prev) => (prev - 1 + commentsData.length) % commentsData.length);
  };

  const handleNextComment = () => {
    setCommentIndex((prev) => (prev + 1) % commentsData.length);
  };

  return (
    <section className="py-16 md:py-20 bg-background-100">
      <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
        {/* Tab Switcher */}
        <div className="flex items-center justify-center mb-10">
          <div className="inline-flex rounded-full bg-background-200/70 p-1">
            <button
              onClick={() => setActiveTab('review')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeTab === 'review'
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className="ri-star-fill mr-1.5 text-xs"></i>
              主编锐评
            </button>
            <button
              onClick={() => setActiveTab('comments')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeTab === 'comments'
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className="ri-chat-smile-2-line mr-1.5 text-xs"></i>
              网友神评
            </button>
          </div>
        </div>

        {activeTab === 'review' ? (
          /* Editor Review */
          <div className="text-center">
            {/* Title */}
            <span className="text-foreground-400 font-label text-xs tracking-widest uppercase mb-4 block">
              {editorReview.title}
            </span>

            {/* Rating Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-100 text-accent-900 mb-6">
              <i className="ri-star-fill text-accent-500"></i>
              <span className="font-heading font-bold text-lg">{editorReview.rating}</span>
              <span className="text-accent-700 text-sm">/ 5.0</span>
            </div>

            {/* Quote */}
            <blockquote className="font-heading text-foreground-800 text-base md:text-lg leading-relaxed italic mb-8">
              &ldquo;{editorReview.content}&rdquo;
            </blockquote>

            {/* Editor Info */}
            <div className="flex items-center justify-center gap-3">
              <div className="w-12 h-12 rounded-full bg-secondary-300 flex items-center justify-center overflow-hidden">
                <i className="ri-user-3-line text-foreground-500 text-xl"></i>
              </div>
              <div className="text-left">
                <p className="text-foreground-900 font-medium text-sm">{editorReview.editorName}</p>
                <p className="text-foreground-500 text-xs">{editorReview.editorTitle}</p>
              </div>
            </div>
          </div>
        ) : (
          /* User Comments */
          <div>
            {/* Comment Card */}
            <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
              {/* Comment Header */}
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-medium mb-6">
                <i className="ri-fire-line text-accent-500"></i>
                炸裂指数 5.0
              </div>

              {/* Comment Content */}
              <blockquote className="font-heading text-foreground-800 text-base md:text-lg leading-relaxed italic mb-6">
                &ldquo;{currentComment.content}&rdquo;
              </blockquote>

              {/* User Info */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-200 flex items-center justify-center overflow-hidden">
                    <i className="ri-user-smile-line text-foreground-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-foreground-900 font-medium text-sm">{currentComment.userName}</p>
                    <p className="text-foreground-500 text-xs">
                      #{currentComment.floor}楼 · {currentComment.likes.toLocaleString()} 赞 · {currentComment.time}
                    </p>
                  </div>
                </div>

                {/* Nav Arrows */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePrevComment}
                    className="w-9 h-9 rounded-lg border border-foreground-300 flex items-center justify-center text-foreground-500 hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all duration-200 cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  <button
                    onClick={handleNextComment}
                    className="w-9 h-9 rounded-lg bg-foreground-950 flex items-center justify-center text-background-50 hover:bg-foreground-800 transition-all duration-200 cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Pagination Dots */}
            <div className="flex items-center justify-center gap-1.5 mt-4">
              {commentsData.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCommentIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 cursor-pointer ${
                    i === commentIndex
                      ? 'bg-primary-500 w-6'
                      : 'bg-background-300 hover:bg-background-400'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}